import { useState, useEffect } from 'react';

/**
 * Categorize a school based on student's scores compared to school's score ranges
 * Returns 'reach', 'target', or 'safety'
 */
function categorizeSchool(school, studentScores) {
  const { satScore, actScore } = studentScores;

  // Try SAT-based categorization first
  if (satScore && school.sat_25th && school.sat_75th) {
    if (satScore >= school.sat_75th) {
      return 'safety';
    } else if (satScore >= school.sat_25th) {
      return 'target';
    } else {
      return 'reach';
    }
  }

  // Try ACT-based categorization
  if (actScore && school.act_25th && school.act_75th) {
    if (actScore >= school.act_75th) {
      return 'safety';
    } else if (actScore >= school.act_25th) {
      return 'target';
    } else {
      return 'reach';
    }
  }

  // Fallback to acceptance rate-based categorization
  // Using backend thresholds: ≤15% = reach, ≥80% = safety, 15-80% = target
  const acceptanceRate = school.acceptance_rate;
  if (acceptanceRate !== null && acceptanceRate !== undefined) {
    if (acceptanceRate <= 15) {
      return 'reach';
    } else if (acceptanceRate >= 80) {
      return 'safety';
    } else {
      return 'target';
    }
  }

  // Last resort fallback
  return 'target';
}

/**
 * Custom hook for managing saved colleges
 */
export function useSavedColleges(recommendations, schoolPreferences, formData = {}) {
  const [savedColleges, setSavedColleges] = useState([]);

  // Automatically populate savedColleges with user's originally selected schools AND loved schools
  useEffect(() => {
    if (!recommendations) return;

    const lovedSchools = [];
    const studentScores = {
      satScore: formData?.satScore,
      actScore: formData?.actScore
    };

    // Get old admission chances map if available (from profile edit)
    const oldChances = recommendations.old_admission_chances || {};

    // Get user's originally selected schools
    if (recommendations.user_schools_analysis && recommendations.user_schools_analysis.length > 0) {
      const userSchools = recommendations.user_schools_analysis
        .filter(school => school.found_in_db)
        .map(school => {
          // Use provided category if valid, otherwise calculate it
          let category = school.category;
          if (!category || category === 'unknown') {
            category = categorizeSchool(school, studentScores);
          }

          return {
            id: school.id,
            name: school.name,
            city: school.city,
            state: school.state,
            sat_range: school.sat_range,
            act_range: school.act_range,
            acceptance_rate: school.acceptance_rate,
            url: school.url,
            category: category,
            sat_25th: school.sat_25th,
            sat_75th: school.sat_75th,
            act_25th: school.act_25th,
            act_75th: school.act_75th,
            based_on_general_rate: school.based_on_general_rate || !school.category,
            admission_chances: school.admission_chances,
            old_admission_chances: oldChances[String(school.id)]
          };
        });
      lovedSchools.push(...userSchools);
    }

    // Add any schools that were hearted (have 'loved' preference)
    if (schoolPreferences && Object.keys(schoolPreferences).length > 0) {
      // Find schools that are loved but not already in the list
      // Check each category separately to preserve the category info
      Object.keys(schoolPreferences).forEach(schoolId => {
        if (schoolPreferences[schoolId] === 'loved') {
          const parsedId = parseInt(schoolId);

          // Skip if already in lovedSchools
          if (lovedSchools.some(s => s.id === parsedId)) return;

          // Check each category to find the school and preserve category
          let foundSchool = null;
          let foundCategory = null;

          const reachSchool = (recommendations.reach || []).find(s => s.id === parsedId);
          if (reachSchool) {
            foundSchool = reachSchool;
            foundCategory = 'reach';
          }

          if (!foundSchool) {
            const targetSchool = (recommendations.target || []).find(s => s.id === parsedId);
            if (targetSchool) {
              foundSchool = targetSchool;
              foundCategory = 'target';
            }
          }

          if (!foundSchool) {
            const safetySchool = (recommendations.safety || []).find(s => s.id === parsedId);
            if (safetySchool) {
              foundSchool = safetySchool;
              foundCategory = 'safety';
            }
          }

          if (foundSchool) {
            lovedSchools.push({
              ...foundSchool,
              category: foundCategory,
              old_admission_chances: oldChances[String(foundSchool.id)]
            });
          }
        }
      });
    }

    // Sort by category: reach, target, safety, unknown
    const categoryOrder = { reach: 1, target: 2, safety: 3, unknown: 4 };
    lovedSchools.sort((a, b) => {
      const orderA = categoryOrder[a.category] || 4;
      const orderB = categoryOrder[b.category] || 4;
      return orderA - orderB;
    });

    setSavedColleges(lovedSchools);
  }, [recommendations, schoolPreferences, formData]);

  const addToSaved = (school, category) => {
    if (!savedColleges.some(s => s.id === school.id)) {
      setSavedColleges(prev => {
        // Enrich the school object with the category if provided
        const schoolWithCategory = category ? { ...school, category } : school;
        const updated = [...prev, schoolWithCategory];
        // Sort by category: reach, target, safety, unknown
        const categoryOrder = { reach: 1, target: 2, safety: 3, unknown: 4 };
        updated.sort((a, b) => {
          const orderA = categoryOrder[a.category] || 4;
          const orderB = categoryOrder[b.category] || 4;
          return orderA - orderB;
        });
        return updated;
      });
    }
  };

  const removeFromSaved = (schoolId) => {
    const school = savedColleges.find(s => s.id === schoolId);
    if (school && window.confirm(`Are you sure you want to remove ${school.name} from your saved colleges?`)) {
      setSavedColleges(prev => prev.filter(s => s.id !== schoolId));
      return true;
    }
    return false;
  };

  return {
    savedColleges,
    setSavedColleges,
    addToSaved,
    removeFromSaved
  };
}